import { ROUTES } from '../lib/routes';
import { navigate } from '../router';
import { showModal, showToast } from '../components/toast';
import { el } from '../utils/dom';
import {
  fetchServiceApplicationByNo,
  submitServiceApplication,
} from '../api/serviceApplicationPortal';

const SERVICE_TYPES: [string, string][] = [
  ['EVOS-T', 'EVOS-T'],
  ['EVOS-M', 'EVOS-M'],
  ['EVOS-A', 'EVOS-A'],
  ['EVOST', 'EVOST'],
  ['EVOSA', 'EVOSA'],
  ['기타 / 미정', '기타'],
];

function textField(label: string, type: string, placeholder: string, required: boolean): HTMLElement {
  const wrap = el('div', { className: 'ev123-field' });
  const input = el('input', { className: 'ev123-input', type, placeholder, required }) as HTMLInputElement;
  wrap.append(el('label', { className: 'ev123-label', text: label }), input);
  return wrap;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return '접수완료 · 승인대기';
    case 'approved':
      return '승인됨 · 개통 대기 (서비스 이용 전)';
    case 'rejected':
      return '반려됨';
    case 'activated':
      return '개통 완료 · 서비스 이용 가능';
    default:
      return status;
  }
}

export function renderServiceApplicationPage(root: HTMLElement): void {
  const wrap = el('div', {});

  wrap.append(
    el('div', { className: 'ev123-page-head' }, [
      el('div', { className: 'ev123-container' }, [
        el('h1', { className: 'ev123-page-head__title', text: 'EV 서비스 도입 신청' }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '이 페이지는 회원가입이 아니라, EV 플랫폼 서비스 도입을 위한 공식 신청 접수입니다. 접수 후 내부 검토·승인·개통 절차를 거친 뒤에만 서비스를 이용하실 수 있습니다. 즉시 이용 가능하지 않습니다.',
        }),
      ]),
    ])
  );

  const container = el('div', { className: 'ev123-container ev123-stack' });
  const card = el('div', { className: 'ev123-card' });

  const serviceSel = el('select', { className: 'ev123-select', required: true }) as HTMLSelectElement;
  serviceSel.appendChild(el('option', { value: '', text: '신청 서비스 유형' }));
  for (const [v, t] of SERVICE_TYPES) {
    serviceSel.appendChild(el('option', { value: v, text: t }));
  }

  const companyName = textField('회사(법인)명', 'text', '사업자등록증 기준', true);
  const bizNo = textField('사업자등록번호', 'text', '000-00-00000', true);
  const applicantName = textField('담당자 이름', 'text', '실명', true);
  const phone = textField('연락처', 'tel', '010-0000-0000', true);
  const email = textField('이메일', 'email', 'name@example.com', true);
  const siteName = textField('현장(사이트)명', 'text', '아파트·상업시설 등', true);
  const siteAddress = textField('현장 주소', 'text', '도로명 주소', true);
  const stationIdsField = textField('충전기 ID (선택)', 'text', '여러 개는 쉼표로 구분', false);

  const memoWrap = el('div', { className: 'ev123-field' });
  const memo = el('textarea', {
    className: 'ev123-input',
    rows: '4',
    placeholder: '요청 사항·현장 상황 등 (선택)',
  }) as HTMLTextAreaElement;
  memoWrap.append(el('label', { className: 'ev123-label', text: '요청 메모' }), memo);

  card.append(
    el('div', { className: 'ev123-field' }, [
      el('label', { className: 'ev123-label', text: '서비스 유형' }),
      serviceSel,
    ]),
    companyName,
    bizNo,
    applicantName,
    phone,
    email,
    siteName,
    siteAddress,
    stationIdsField,
    memoWrap
  );

  const actions = el('div', { className: 'ev123-stack', style: 'margin-top:1rem;gap:0.75rem' });
  const submit = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--block',
  }) as HTMLButtonElement;
  submit.textContent = '신청 접수하기';

  const hint = el('p', {
    className: 'ev123-field-hint',
    text: '포털 회원가입·로그인과는 별도 단계입니다. 사이트 이용 계정은 회원가입 메뉴에서 신청해 주세요.',
  });

  const lookupCard = el('div', { className: 'ev123-card ev123-card--future' });
  lookupCard.append(
    el('h2', { className: 'ev123-card__title ev123-card__title--sub', text: '접수번호로 진행 상태 조회' }),
    el('p', {
      className: 'ev123-card__desc ev123-card__desc--sub',
      text: '접수 완료 시 발급된 접수번호로 현재 상태만 확인할 수 있습니다. (내부 메모·개인정보는 표시되지 않습니다.)',
    })
  );
  const lookupRow = el('div', { className: 'ev123-field' });
  const lookupInput = el('input', {
    className: 'ev123-input',
    type: 'text',
    placeholder: '예: EVS20260419-ABCDEF',
  }) as HTMLInputElement;
  lookupRow.append(el('label', { className: 'ev123-label', text: '접수번호' }), lookupInput);
  const lookupBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--line ev123-btn--block',
    style: 'margin-top:0.75rem',
  }) as HTMLButtonElement;
  lookupBtn.textContent = '조회';
  const lookupResult = el('div', {
    className: 'ev123-field-hint',
    style: 'margin-top:0.75rem;white-space:pre-wrap',
  });
  lookupCard.append(lookupRow, lookupBtn, lookupResult);

  submit.addEventListener('click', async () => {
    const q = (field: HTMLElement) => field.querySelector('input') as HTMLInputElement | null;

    const cn = q(companyName)?.value?.trim() ?? '';
    const bn = q(bizNo)?.value?.trim() ?? '';
    const an = q(applicantName)?.value?.trim() ?? '';
    const ph = q(phone)?.value?.trim() ?? '';
    const em = q(email)?.value?.trim() ?? '';
    const sn = q(siteName)?.value?.trim() ?? '';
    const sa = q(siteAddress)?.value?.trim() ?? '';
    const stRaw = q(stationIdsField)?.value?.trim() ?? '';

    if (!serviceSel.value) {
      showToast('서비스 유형을 선택해 주세요.');
      return;
    }
    if (!cn || !bn || !an || !ph || !em || !sn || !sa) {
      showToast('필수 항목을 모두 입력해 주세요.');
      return;
    }

    const stationIds = stRaw
      ? stRaw
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    submit.disabled = true;
    const prev = submit.textContent;
    submit.textContent = '접수 중…';
    try {
      const res = await submitServiceApplication({
        serviceType: serviceSel.value,
        companyName: cn,
        bizNo: bn,
        applicantName: an,
        phone: ph,
        email: em,
        siteName: sn,
        siteAddress: sa,
        stationIds: stationIds.length ? stationIds : null,
        memoFromApplicant: memo.value.trim() || null,
      });
      showModal({
        title: '접수 완료',
        body: `접수번호: ${res.applicationNo}\n상태: ${statusLabel(res.status)}\n\n내부 검토 후 승인·개통이 이루어져야 서비스를 이용하실 수 있습니다. 접수번호를 잘 보관해 주세요.`,
        confirmLabel: '확인',
        onClose: () => navigate(ROUTES.homePublic),
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : '신청에 실패했습니다.');
    } finally {
      submit.disabled = false;
      submit.textContent = prev ?? '신청 접수하기';
    }
  });

  lookupBtn.addEventListener('click', async () => {
    const no = lookupInput.value.trim();
    if (!no) {
      showToast('접수번호를 입력해 주세요.');
      return;
    }
    lookupBtn.disabled = true;
    const prevL = lookupBtn.textContent;
    lookupBtn.textContent = '조회 중…';
    lookupResult.textContent = '';
    try {
      const d = await fetchServiceApplicationByNo(no);
      const lines = [
        `상태: ${statusLabel(d.status)}`,
        `서비스: ${d.serviceType}`,
        `현장: ${d.siteName}`,
        d.rejectionReason ? `반려 사유: ${d.rejectionReason}` : '',
      ].filter(Boolean);
      lookupResult.textContent = lines.join('\n');
    } catch (e) {
      lookupResult.textContent = e instanceof Error ? e.message : '조회에 실패했습니다.';
    } finally {
      lookupBtn.disabled = false;
      lookupBtn.textContent = prevL ?? '조회';
    }
  });

  actions.append(submit, hint);
  card.appendChild(actions);
  container.append(card, lookupCard);
  wrap.appendChild(container);
  root.appendChild(wrap);
}
